import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    MdKey,
    MdWebhook,
    MdHealthAndSafety,
    MdContentCopy,
    MdRefresh,
    MdAdd,
    MdDelete,
    MdEdit,
    MdPlayArrow,
    MdCheckCircle,
    MdError,
    MdWarning,
    MdCode,
} from 'react-icons/md';
import { fetchCustomIntegratorConfig, rotateCredentials, fetchWebhooks, createWebhook, updateWebhook, deleteWebhook, testWebhook, fetchIntegrationHealth, fetchIntegrationLogs } from '../../../services/Http/CustomIntegratorApi';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import MainButton from '../../../shared/layout/buttons/MainButton';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import { SimpleSelect } from '../../../shared/layout/forms/AppSelect';
import customIntegratorOpenApi from './CustomIntegratorOpenApi';

const SwaggerUI = React.lazy(() => import('swagger-ui-react'));

const CustomIntegratorPanel = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [activeSection, setActiveSection] = useState('credentials');

    // Credentials state
    const [apiKey, setApiKey] = useState('');
    const [apiSecret, setApiSecret] = useState('');
    const [showSecret, setShowSecret] = useState(false);
    const [rotating, setRotating] = useState(false);
    const [credentialsRevision, setCredentialsRevision] = useState(0);

    // Webhooks state
    const [webhooks, setWebhooks] = useState([]);
    const [webhookModal, setWebhookModal] = useState(false);
    const [currentWebhook, setCurrentWebhook] = useState(null);
    const [webhookForm, setWebhookForm] = useState({
        url: '',
        events: [],
        active: true,
    });

    // Health state
    const [health, setHealth] = useState(null);

    // Logs state
    const [logs, setLogs] = useState([]);
    const [logFilter, setLogFilter] = useState('all');
    const [logsLoading, setLogsLoading] = useState(false);
    const [logsError, setLogsError] = useState(false);

    // Available webhook events
    const webhookEvents = [
        'warranty.activated',
        'warranty.claimed',
        'warranty.expired',
        'product.synced',
        'transaction.processed',
        'order.created',
        'order.paid',
    ];

    useEffect(() => {
        loadCredentials();
        loadWebhooks();
        loadHealth();
    }, []);

    useEffect(() => {
        let active = true;
        setLogsLoading(true);
        setLogsError(false);
        setLogs([]);
        fetchIntegrationLogs(logFilter === 'all' ? '' : logFilter)
            .then((data) => { if (active) setLogs(data?.data || []); })
            .catch(() => { if (active) setLogsError(true); })
            .finally(() => { if (active) setLogsLoading(false); });
        return () => { active = false; };
    }, [logFilter, credentialsRevision]);

    const loadCredentials = async () => {
        try {
            const config = await fetchCustomIntegratorConfig();
            setApiKey(config?.apiKey || '');
            setApiSecret(config?.apiSecret || '');
            setLoading(false);
        } catch (err) {
            toast.error(t('CustomIntegratorPanel.errors.loadCredentials'));
            setLoading(false);
        }
    };

    const loadWebhooks = async () => {
        try {
            const data = await fetchWebhooks();
            setWebhooks(Array.isArray(data) ? data : data?.data || []);
        } catch (err) {
            console.error(err);
        }
    };

    const loadHealth = async () => {
        try {
            const data = await fetchIntegrationHealth();
            setHealth(data);
        } catch (err) {
            console.error(err);
        }
    };

    const handleCopyToClipboard = (text, label) => {
        navigator.clipboard.writeText(text);
        toast.success(t('CustomIntegratorPanel.toasts.copiedToClipboard', { label }));
    };

    const handleRotateCredentials = async () => {
        if (rotating) return;
        if (!window.confirm(t('CustomIntegratorPanel.confirm.rotateCredentials'))) {
            return;
        }

        setRotating(true);
        try {
            const newCreds = await rotateCredentials();
            setApiKey(newCreds.apiKey);
            setApiSecret(newCreds.apiSecret);
            setShowSecret(true);
            setCredentialsRevision((revision) => revision + 1);
            loadWebhooks();
            loadHealth();
            toast.success(t('CustomIntegratorPanel.toasts.credentialsRotated'));
            toast.warning(t('CustomIntegratorPanel.toasts.updateIntegrationNow'), { autoClose: 10000 });
        } catch (err) {
            toast.error(t('CustomIntegratorPanel.errors.rotateCredentials'));
        } finally {
            setRotating(false);
        }
    };

    const handleCreateWebhook = () => {
        setCurrentWebhook(null);
        setWebhookForm({ url: '', events: [], active: true });
        setWebhookModal(true);
    };

    const handleEditWebhook = (webhook) => {
        setCurrentWebhook(webhook);
        setWebhookForm({
            url: webhook.url,
            events: webhook.events || [],
            active: webhook.is_active,
        });
        setWebhookModal(true);
    };

    const handleSaveWebhook = async () => {
        try {
            if (currentWebhook) {
                await updateWebhook(currentWebhook.id, webhookForm);
                toast.success(t('CustomIntegratorPanel.toasts.webhookUpdated'));
            } else {
                await createWebhook(webhookForm);
                toast.success(t('CustomIntegratorPanel.toasts.webhookCreated'));
            }
            setWebhookModal(false);
            loadWebhooks();
        } catch (err) {
            toast.error(t('CustomIntegratorPanel.errors.saveWebhook'));
        }
    };

    const handleDeleteWebhook = async (webhookId) => {
        if (!window.confirm(t('CustomIntegratorPanel.confirm.deleteWebhook'))) {
            return;
        }

        try {
            await deleteWebhook(webhookId);
            toast.success(t('CustomIntegratorPanel.toasts.webhookDeleted'));
            loadWebhooks();
        } catch (err) {
            toast.error(t('CustomIntegratorPanel.errors.deleteWebhook'));
        }
    };

    const handleTestWebhook = async (webhookId) => {
        try {
            const result = await testWebhook(webhookId);
            if (result.success) {
                toast.success(t('CustomIntegratorPanel.toasts.webhookTestSuccess'));
            } else {
                toast.error(t('CustomIntegratorPanel.errors.webhookTestFailedWithReason', { reason: result.test_result?.error || result.message }));
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.response?.data?.error || t('CustomIntegratorPanel.errors.testWebhook'));
        }
    };

    const toggleWebhookEvent = (event) => {
        setWebhookForm((prev) => {
            const events = prev.events || [];
            if (events.includes(event)) {
                return { ...prev, events: events.filter((e) => e !== event) };
            } else {
                return { ...prev, events: [...events, event] };
            }
        });
    };

    if (loading) {
        return <LoadingMessage>{t('Common.loading')}</LoadingMessage>;
    }

    return (
        <Container>
            <SectionLayout>
                <SectionTabs>
                    <SectionTab $active={activeSection === 'credentials'} onClick={() => setActiveSection('credentials')}>
                        <MdKey size={18} /> {t('CustomIntegratorPanel.tabs.credentials')}
                    </SectionTab>
                    <SectionTab $active={activeSection === 'webhooks'} onClick={() => setActiveSection('webhooks')}>
                        <MdWebhook size={18} /> {t('CustomIntegratorPanel.tabs.webhooks')}
                    </SectionTab>
                    <SectionTab $active={activeSection === 'health'} onClick={() => setActiveSection('health')}>
                        <MdHealthAndSafety size={18} /> {t('CustomIntegratorPanel.tabs.healthMonitoring')}
                    </SectionTab>
                    <SectionTab $active={activeSection === 'docs'} onClick={() => setActiveSection('docs')}>
                        <MdCode size={18} /> Developer API
                    </SectionTab>
                </SectionTabs>

                <SectionContent>
                    {activeSection === 'credentials' && (
                        <StyledCard title={t('CustomIntegratorPanel.credentials.title')} subtitle={t('CustomIntegratorPanel.credentials.subtitle')}>
                            <CredentialGroup>
                                <Label>{t('CustomIntegratorPanel.credentials.apiKey')}</Label>
                                <InputGroup>
                                    <StyledInput readOnly value={apiKey || t('CustomIntegratorPanel.credentials.notGenerated')} />
                                    {apiKey && (
                                        <MainButton onClick={() => handleCopyToClipboard(apiKey, t('CustomIntegratorPanel.credentials.apiKey'))} style={{ width: 'auto' }}>
                                            <MdContentCopy />
                                        </MainButton>
                                    )}
                                </InputGroup>
                            </CredentialGroup>

                            <CredentialGroup>
                                <Label>{t('CustomIntegratorPanel.credentials.apiSecret')}</Label>
                                <InputGroup>
                                    <StyledInput readOnly value={apiSecret ? (showSecret ? apiSecret : '••••••••••••••••••••') : t('CustomIntegratorPanel.credentials.notGenerated')} />
                                    {apiSecret && (
                                        <>
                                            <MainButton onClick={() => setShowSecret(!showSecret)} style={{ width: 'auto' }}>
                                                {showSecret ? t('Common.hide') : t('Common.show')}
                                            </MainButton>
                                            <MainButton onClick={() => handleCopyToClipboard(apiSecret, t('CustomIntegratorPanel.credentials.apiSecret'))} style={{ width: 'auto' }}>
                                                <MdContentCopy />
                                            </MainButton>
                                        </>
                                    )}
                                </InputGroup>
                            </CredentialGroup>

                            <ActionRow>
                                <MainButton onClick={handleRotateCredentials} disabled={rotating} $variant="danger">
                                    <MdRefresh size={18} style={{ marginRight: '8px' }} /> {t('CustomIntegratorPanel.actions.rotateCredentials')}
                                </MainButton>
                            </ActionRow>

                            <Alert>
                                <MdWarning size={20} />
                                <div>
                                    <strong>{t('CustomIntegratorPanel.security.title')}</strong>
                                    <p>{t('CustomIntegratorPanel.security.body')}</p>
                                </div>
                            </Alert>

                            <DocsSection>
                                <h4>{t('CustomIntegratorPanel.docs.quickStart')}</h4>
                                <CodeBlock>
                                    {`// Example: Create a product
POST https://api.digiwarranty.app/api/v1/integrator/products

Headers:
  X-API-KEY: ${apiKey || 'your-api-key'}
  X-API-SECRET: ${showSecret && apiSecret ? apiSecret : 'your-api-secret'}
  Content-Type: application/json

Body:
{
  "external_id": "prod_123",
  "sku": "SKU-001",
  "name": "Laptop Computer",
  "brand": "TechBrand",
  "price": 999.99
}`}
                                </CodeBlock>
                                <DocsLink href="https://docs.digiwarranty.app/api" target="_blank">
                                    {t('CustomIntegratorPanel.docs.viewFull')}
                                </DocsLink>
                            </DocsSection>
                        </StyledCard>
                    )}

                    {activeSection === 'webhooks' && (
                        <StyledCard>
                            <ActionRow>
                                <MainButton onClick={handleCreateWebhook} disabled={!apiKey}><MdAdd size={18} /> {t('CustomIntegratorPanel.webhooks.addWebhook')}</MainButton>
                            </ActionRow>
                            {webhooks.length === 0 ? (
                                <EmptyState>
                                    <MdWebhook size={48} />
                                    <p>{t('CustomIntegratorPanel.webhooks.emptyTitle')}</p>
                                    <small>{t('CustomIntegratorPanel.webhooks.emptySubtitle')}</small>
                                </EmptyState>
                            ) : (
                                <WebhookList>
                                    {webhooks.map((webhook) => (
                                        <WebhookCard key={webhook.id}>
                                            <WebhookHeader>
                                                <StatusBadge $active={webhook.is_active}>
                                                    {webhook.is_active ? <MdCheckCircle /> : <MdError />}
                                                    {webhook.is_active ? t('Common.active') : t('Common.inactive')}
                                                </StatusBadge>
                                                <WebhookActions>
                                                    <IconButton onClick={() => handleTestWebhook(webhook.id)} title={t('CustomIntegratorPanel.webhooks.actions.test')}>
                                                        <MdPlayArrow size={18} />
                                                    </IconButton>
                                                    <IconButton onClick={() => handleEditWebhook(webhook)} title={t('CustomIntegratorPanel.webhooks.actions.edit')}>
                                                        <MdEdit size={18} />
                                                    </IconButton>
                                                    <IconButton onClick={() => handleDeleteWebhook(webhook.id)} $danger title={t('CustomIntegratorPanel.webhooks.actions.delete')}>
                                                        <MdDelete size={18} />
                                                    </IconButton>
                                                </WebhookActions>
                                            </WebhookHeader>

                                            <WebhookUrl>{webhook.url}</WebhookUrl>

                                            <WebhookMeta>
                                                <span><strong>{t('CustomIntegratorPanel.webhooks.meta.events')}:</strong> {webhook.events?.join(', ') || t('CustomIntegratorPanel.webhooks.meta.allEvents')}</span>
                                                <span><strong>{t('CustomIntegratorPanel.webhooks.meta.lastDelivery')}:</strong> {webhook.last_delivery_at || t('CustomIntegratorPanel.webhooks.meta.never')}</span>
                                                <span>
                                                    <strong>{t('CustomIntegratorPanel.webhooks.meta.successRate')}:</strong>{' '}
                                                    {t('Common.notAvailable')}
                                                </span>
                                            </WebhookMeta>
                                        </WebhookCard>
                                    ))}
                                </WebhookList>
                            )}
                        </StyledCard>
                    )}

                    {activeSection === 'health' && (
                        <StyledCard title={t('CustomIntegratorPanel.health.title')} subtitle={t('CustomIntegratorPanel.health.subtitle')}>
                            {health && (
                                <>
                                    <StatsGrid>
                                        <StatCard>
                                            <StatNumber>{health.stats?.products_synced ?? t('Common.notAvailable')}</StatNumber>
                                            <StatLabel>{t('CustomIntegratorPanel.health.productsSynced')}</StatLabel>
                                        </StatCard>
                                        <StatCard>
                                            <StatNumber>{health.stats?.transactions_processed ?? t('Common.notAvailable')}</StatNumber>
                                            <StatLabel>{t('CustomIntegratorPanel.health.transactionsProcessed')}</StatLabel>
                                        </StatCard>
                                        <StatCard>
                                            <StatNumber>{health.stats?.customers_created ?? t('Common.notAvailable')}</StatNumber>
                                            <StatLabel>{t('CustomIntegratorPanel.health.customersCreated')}</StatLabel>
                                        </StatCard>
                                        <StatCard $error={health.stats?.errors_last_24h > 0}>
                                            <StatNumber>{health.stats?.errors_last_24h ?? t('Common.notAvailable')}</StatNumber>
                                            <StatLabel>{t('CustomIntegratorPanel.health.errors24h')}</StatLabel>
                                        </StatCard>
                                    </StatsGrid>

                                    <LogsSection>
                                        <LogsHeader>
                                            <h4>{t('CustomIntegratorPanel.health.recentActivity')}</h4>
                                            <StyledSelect value={logFilter} onChange={(e) => setLogFilter(e.target.value)}>
                                                <option value="all">{t('CustomIntegratorPanel.health.logTypes.all')}</option>
                                                <option value="request">{t('CustomIntegratorPanel.health.logTypes.request')}</option>
                                                <option value="error">{t('CustomIntegratorPanel.health.logTypes.error')}</option>
                                                <option value="webhook">{t('CustomIntegratorPanel.health.logTypes.webhook')}</option>
                                            </StyledSelect>
                                        </LogsHeader>

                                        <LogsList>
                                            {logsLoading ? <LoadingMessage>{t('Settings.loading')}</LoadingMessage> : logsError ? (
                                                <LogError role="alert">{t('Settings.Integrations.error')}</LogError>
                                            ) : logs.length === 0 ? (
                                                <EmptyState>
                                                    <p>{t('CustomIntegratorPanel.health.noActivity')}</p>
                                                </EmptyState>
                                            ) : (
                                                logs.map((log) => (
                                                    <LogItem key={log.id} $error={log.log_type === 'error'}>
                                                        <LogIcon>
                                                            {log.status_code >= 200 && log.status_code < 300 ? (
                                                                <MdCheckCircle color="green" />
                                                            ) : (
                                                                <MdError color="red" />
                                                            )}
                                                        </LogIcon>
                                                        <LogContent>
                                                            <LogTitle>
                                                                {log.method} {log.endpoint}
                                                            </LogTitle>
                                                            <LogMeta>
                                                                {log.created_at} • {log.status_code || t('Common.notAvailable')} • {log.duration_ms}ms
                                                            </LogMeta>
                                                            {log.error_message && <LogError>{log.error_message}</LogError>}
                                                        </LogContent>
                                                    </LogItem>
                                                ))
                                            )}
                                        </LogsList>
                                    </LogsSection>
                                </>
                            )}
                        </StyledCard>
                    )}

                    {activeSection === 'docs' && (
                        <StyledCard title="Developer API" subtitle="Test the DigiWarranty Custom Integrator API with the credentials above.">
                            <ApiDocsNotice>
                                Use <strong>Authorize</strong> and enter the API key and secret shown in the Credentials section. Swagger requests run against the configured API server and can create real records.
                            </ApiDocsNotice>
                            <SwaggerWrapper>
                                <React.Suspense fallback={<LoadingMessage>{t('Common.loading')}</LoadingMessage>}>
                                    <SwaggerUI spec={customIntegratorOpenApi} persistAuthorization={false} docExpansion="list" defaultModelsExpandDepth={-1} />
                                </React.Suspense>
                            </SwaggerWrapper>
                        </StyledCard>
                    )}
                </SectionContent>
            </SectionLayout>

            {webhookModal && (
                <ModalOverlay onClick={() => setWebhookModal(false)}>
                    <ModalContent onClick={e => e.stopPropagation()}>
                        <ModalHeader>
                            <h4>{currentWebhook ? t('CustomIntegratorPanel.webhooks.modal.editTitle') : t('CustomIntegratorPanel.webhooks.modal.createTitle')}</h4>
                            <CloseButton onClick={() => setWebhookModal(false)}>×</CloseButton>
                        </ModalHeader>
                        <ModalBody>
                            <FormGroup>
                                <Label>{t('CustomIntegratorPanel.webhooks.modal.webhookUrl')}</Label>
                                <StyledInput
                                    type="url"
                                    placeholder={t('CustomIntegratorPanel.webhooks.modal.webhookUrlPlaceholder')}
                                    value={webhookForm.url}
                                    onChange={(e) => setWebhookForm({ ...webhookForm, url: e.target.value })}
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label>{t('CustomIntegratorPanel.webhooks.modal.eventsToSubscribe')}</Label>
                                <EventGrid>
                                    {webhookEvents.map((event) => (
                                        <EventCheckbox key={event}>
                                            <input
                                                type="checkbox"
                                                checked={webhookForm.events.includes(event)}
                                                onChange={() => toggleWebhookEvent(event)}
                                            />
                                            <span>{event}</span>
                                        </EventCheckbox>
                                    ))}
                                </EventGrid>
                            </FormGroup>

                            <FormGroup>
                                <EventCheckbox>
                                    <input
                                        type="checkbox"
                                        checked={webhookForm.active}
                                        onChange={(e) => setWebhookForm({ ...webhookForm, active: e.target.checked })}
                                    />
                                    <span>{t('Common.active')}</span>
                                </EventCheckbox>
                            </FormGroup>
                        </ModalBody>
                        <ModalFooter>
                            <MainButton onClick={() => setWebhookModal(false)} $variant="secondary">{t('Common.cancel')}</MainButton>
                            <MainButton onClick={handleSaveWebhook}>{t('CustomIntegratorPanel.webhooks.modal.saveWebhook')}</MainButton>
                        </ModalFooter>
                    </ModalContent>
                </ModalOverlay>
            )}
        </Container>
    );
};

export default CustomIntegratorPanel;

// Styled Components
const Container = styled.div`
    width: 100%;
`;

const ApiDocsNotice = styled.div`
    margin-bottom: 1rem;
    padding: 0.85rem 1rem;
    border-left: 3px solid #701a82;
    background: rgba(112, 26, 130, 0.06);
    color: #4b5563;
    line-height: 1.5;
`;

const SwaggerWrapper = styled.div`
    overflow-x: auto;
    background: #fff;

    .swagger-ui .topbar {
        display: none;
    }

    .swagger-ui .info {
        margin: 0 0 1rem;
    }

    .swagger-ui .wrapper {
        padding: 0;
    }
`;

const SectionLayout = styled.div`
    display: grid;
    grid-template-columns: 240px 1fr;
    gap: 1.5rem;
    align-items: start;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

const SectionTabs = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const SectionTab = styled.button`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border: 1px solid ${({ $active }) => ($active ? '#701a82' : 'transparent')};
    background: ${({ $active }) => ($active ? 'rgba(112, 26, 130, 0.05)' : 'transparent')};
    color: ${({ $active }) => ($active ? '#701a82' : '#6b7280')};
    font-weight: 500;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    text-align: left;

    &:hover {
        background: ${({ $active }) => ($active ? 'rgba(112, 26, 130, 0.05)' : '#f9fafb')};
        color: #701a82;
    }
`;

const SectionContent = styled.div`
    flex: 1;
`;

const CredentialGroup = styled.div`
    margin-bottom: 1.25rem;
`;

const Label = styled.label`
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.5rem;
    display: block;
`;

const InputGroup = styled.div`
    display: flex;
    gap: 0.5rem;
`;

const StyledInput = styled.input`
    width: 100%;
    padding: 0.625rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.875rem;
    color: #111827;
    outline: none;
    transition: all 0.2s;
    background: ${({ readOnly }) => (readOnly ? '#f9fafb' : '#fff')};
    font-family: ${({ readOnly }) => (readOnly ? "'Monaco', 'Menlo', monospace" : 'inherit')};

    &:focus {
        border-color: #701a82;
        box-shadow: 0 0 0 3px rgba(112, 26, 130, 0.1);
    }
`;

const StyledSelect = styled(SimpleSelect)`
    padding: 0.5rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.875rem;
    color: #111827;
    outline: none;
`;

const ActionRow = styled.div`
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid #e5e7eb;
`;

const Alert = styled.div`
    display: flex;
    gap: 0.9375rem;
    background: #fff8e1;
    border: 1px solid #ffe082;
    border-radius: 8px;
    padding: 1rem;
    margin-top: 1.5rem;
    color: #856404;

    svg {
        flex-shrink: 0;
    }

    strong {
        display: block;
        margin-bottom: 0.25rem;
    }

    p {
        margin: 0;
        font-size: 0.875rem;
        line-height: 1.5;
    }
`;

const DocsSection = styled.div`
    margin-top: 1.5rem;
    background: #f9fafb;
    border-radius: 8px;
    padding: 1.25rem;
    border: 1px solid #e5e7eb;

    h4 {
        margin: 0 0 1rem 0;
        color: #111827;
        font-size: 1rem;
    }
`;

const CodeBlock = styled.pre`
    background: #1f2937;
    color: #e5e7eb;
    padding: 1rem;
    border-radius: 6px;
    overflow-x: auto;
    font-size: 0.8125rem;
    line-height: 1.6;
    margin: 0;
    font-family: 'Monaco', 'Menlo', monospace;
`;

const DocsLink = styled.a`
    display: inline-block;
    margin-top: 1rem;
    color: #701a82;
    font-weight: 500;
    text-decoration: none;
    font-size: 0.875rem;

    &:hover {
        text-decoration: underline;
    }
`;

const EmptyState = styled.div`
    padding: 2.5rem;
    text-align: center;
    color: #6b7280;

    p {
        margin: 0.5rem 0 0.25rem 0;
        font-weight: 500;
        font-size: 1rem;
        color: #111827;
    }

    small {
        font-size: 0.875rem;
    }
`;

const WebhookList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const WebhookCard = styled.div`
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 1rem;
    background: #fff;
    transition: all 0.2s;

    &:hover {
        border-color: #d1d5db;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    }
`;

const WebhookHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.625rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    background: ${({ $active }) => $active ? '#dcfce7' : '#fee2e2'};
    color: ${({ $active }) => $active ? '#166534' : '#991b1b'};
`;

const WebhookActions = styled.div`
    display: flex;
    gap: 0.5rem;
`;

const IconButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
    background: #fff;
    color: ${({ $danger }) => $danger ? '#ef4444' : '#6b7280'};
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: ${({ $danger }) => $danger ? '#fee2e2' : '#f9fafb'};
        color: ${({ $danger }) => $danger ? '#dc2626' : '#111827'};
        border-color: ${({ $danger }) => $danger ? '#fecaca' : '#d1d5db'};
    }
`;

const WebhookUrl = styled.div`
    font-family: 'Monaco', 'Menlo', monospace;
    font-size: 0.875rem;
    color: #111827;
    background: #f9fafb;
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    margin-bottom: 0.75rem;
    word-break: break-all;
`;

const WebhookMeta = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    font-size: 0.8125rem;
    color: #6b7280;

    strong {
        color: #374151;
        font-weight: 500;
    }
`;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
`;

const StatCard = styled.div`
    background: ${({ $error }) => $error ? '#fef2f2' : '#f9fafb'};
    border: 1px solid ${({ $error }) => $error ? '#fee2e2' : '#e5e7eb'};
    border-radius: 8px;
    padding: 1rem;
    text-align: center;
`;

const StatNumber = styled.div`
    font-size: 1.5rem;
    font-weight: 700;
    color: #111827;
    margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
    font-size: 0.8125rem;
    color: #6b7280;
    font-weight: 500;
`;

const LogsSection = styled.div`
    margin-top: 2rem;
`;

const LogsHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;

    h4 {
        margin: 0;
        font-size: 1rem;
        color: #111827;
    }
`;

const LogsList = styled.div`
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    overflow: hidden;
`;

const LogItem = styled.div`
    display: flex;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e5e7eb;
    background: #fff;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: #f9fafb;
    }
`;

const LogIcon = styled.div`
    flex-shrink: 0;
    margin-top: 0.125rem;
`;

const LogContent = styled.div`
    flex: 1;
    min-width: 0;
`;

const LogTitle = styled.div`
    font-family: 'Monaco', 'Menlo', monospace;
    font-size: 0.8125rem;
    color: #111827;
    margin-bottom: 0.25rem;
`;

const LogMeta = styled.div`
    font-size: 0.75rem;
    color: #6b7280;
`;

const LogError = styled.div`
    margin-top: 0.25rem;
    font-size: 0.75rem;
    color: #ef4444;
    font-family: 'Monaco', 'Menlo', monospace;
`;

// Modal Styles
const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const ModalContent = styled.div`
    background: #fff;
    border-radius: 12px;
    width: 90%;
    max-width: 500px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    max-height: 90vh;
`;

const ModalHeader = styled.div`
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    justify-content: space-between;
    align-items: center;

    h4 {
        margin: 0;
        color: #111827;
        font-size: 1.125rem;
    }
`;

const CloseButton = styled.button`
    background: transparent;
    border: none;
    font-size: 1.5rem;
    color: #9ca3af;
    cursor: pointer;
    padding: 0;
    line-height: 1;

    &:hover {
        color: #111827;
    }
`;

const ModalBody = styled.div`
    padding: 1.25rem;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const ModalFooter = styled.div`
    padding: 1rem 1.25rem;
    border-top: 1px solid #e5e7eb;
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
`;

const EventGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.625rem;
    margin-top: 0.5rem;
`;

const EventCheckbox = styled.label`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8125rem;
    color: #374151;
    cursor: pointer;

    input {
        accent-color: #701a82;
    }
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
`;
